import React from "react";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import { Box, Stack, useTheme } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

import Button from "components/basic/button";
import Typography from "components/basic/typography";

import { EVENT_TYPE_MAP } from "pages/dashboard/pages/calendar/eventTypes";
import { CUSTOMER_TYPE_LABELS } from "pages/dashboard/pages/calendar/booking/components/BookAppointmentModal/bookAppointmentApi";
import { QuoteBooking } from "../../bookVisitApi";

const fmtDate = (d: string) =>
  moment(d, "YYYY-MM-DD").format("dddd, MMMM D, YYYY");
const fmtTime = (t: string) => `${moment(t, "HH:mm").format("h:mm A")} (ET)`;

interface BookingConfirmationProps {
  booking: QuoteBooking;
  vendorName: string;
  email: string;
  responding?: boolean;
  onReschedule: () => void;
  onCancel: () => void;
}

// Post-booking summary (per design): green check header, appointment card,
// "what happens next" note, and reschedule / cancel actions.
const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  booking,
  vendorName,
  email,
  responding,
  onReschedule,
  onCancel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const green = theme.palette.success.main;

  const isCall = booking.type === "call";
  const isVideo = booking.type === "video";
  const isCallLike = isCall || isVideo;
  const typeMeta = EVENT_TYPE_MAP[booking.type];
  const TypeIcon = typeMeta.icon;
  const typeLabel = CUSTOMER_TYPE_LABELS[booking.type] ?? typeMeta.label;
  const confirmed = booking.status === "confirmed";
  const rescheduleRequested = booking.myStatus === "reschedule_proposed";

  const subtitle = confirmed
    ? t(isCallLike ? "booking.confirmed_call" : "booking.confirmed_visit", {
        vendor: vendorName,
      })
    : isVideo
      ? t("booking.added_video")
      : t("booking.added", { vendor: vendorName });

  const nextStep = t(
    isCall
      ? "booking.will_call"
      : isVideo
        ? "booking.will_video"
        : "booking.will_meet",
    { vendor: vendorName },
  );

  return (
    <Stack spacing={2} alignItems="stretch">
      {/* Header */}
      <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 44, color: green }} />
        <Typography variant="h4" style={{ fontWeight: 700 }}>
          {t(isCallLike ? "booking.scheduled_call" : "booking.scheduled_visit")}
        </Typography>
        <Typography
          variant="caption"
          style={{ color: theme.palette.text.secondary }}
        >
          {subtitle}
        </Typography>
      </Stack>

      {/* Appointment summary */}
      <Box
        sx={{
          border: `1px solid ${green}55`,
          backgroundColor: `${green}0d`,
          borderRadius: 1.5,
          p: 1.5,
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: green }} />
            <Typography variant="body2">{fmtDate(booking.date)}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 18, color: green }} />
            <Typography variant="body2">{fmtTime(booking.fromTime)}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TypeIcon sx={{ fontSize: 18, color: green }} />
            <Typography variant="body2">{typeLabel}</Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Confirmation email note */}
      <Stack spacing={0.5} alignItems="center" sx={{ textAlign: "center" }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: green }} />
        <Typography
          variant="caption"
          style={{ color: theme.palette.text.secondary }}
        >
          <RichText
            t={t}
            i18nKey="booking.sent_to"
            values={{ email }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Stack>

      {/* Pending reschedule notice */}
      {rescheduleRequested && booking.proposedDate && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.warning.main}66`,
            backgroundColor: `${theme.palette.warning.main}14`,
            borderRadius: 1.5,
            p: 1.5,
          }}
        >
          <Typography variant="caption">
            <RichText
              t={t}
              i18nKey="booking.moved_to"
              values={{
                vendor: vendorName,
                when: `${fmtDate(booking.proposedDate)}${
                  booking.proposedFrom
                    ? `, ${fmtTime(booking.proposedFrom)}`
                    : ""
                }`,
              }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Box>
      )}

      {/* What happens next */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.info.main}44`,
          backgroundColor: `${theme.palette.info.main}0d`,
          borderRadius: 1.5,
          p: 1.5,
        }}
      >
        <Stack direction="row" spacing={1}>
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: theme.palette.info.main, mt: 0.25 }}
          />
          <Box>
            <Typography
              variant="caption"
              style={{ fontWeight: 700, color: theme.palette.info.main }}
            >
              {t("booking.next_title")}
            </Typography>
            <Typography variant="caption" style={{ display: "block" }}>
              {nextStep}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          fullWidth
          disabled={responding}
          startAdornment={<EventRepeatOutlinedIcon fontSize="small" />}
          onClick={onReschedule}
        >
          {t("booking.reschedule")}
        </Button>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          disabled={responding}
          startAdornment={<CloseIcon fontSize="small" />}
          onClick={onCancel}
        >
          {t(isCallLike ? "booking.cancel_call" : "booking.cancel_visit")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default BookingConfirmation;
